<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  // When provided, the modal edits this project (PATCH). Otherwise it creates one (POST).
  project: {
    type: Object,
    default: null,
  },
  // When provided (create mode), the empresa picker is pre-filled and locked to this empresa.
  empresa: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['created', 'updated']);

const store = useStore();
const { t } = useI18n();

const dialogRef = ref(null);

const STATUS_OPTIONS = ['draft', 'active', 'on_hold', 'completed', 'cancelled'];
const HEALTH_OPTIONS = ['unknown', 'green', 'amber', 'red'];
const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'critical'];

const form = ref({
  name: '',
  description: '',
  status: 'draft',
  health: 'unknown',
  priority: 'medium',
  empresa_id: null,
  start_date: '',
  target_end_date: '',
  budget_amount: '',
  playbook_id: null,
});

const isEdit = computed(() => Boolean(props.project?.id));
const uiFlags = computed(() => store.getters.getProjectUIFlags);
const isSaving = computed(() =>
  isEdit.value ? uiFlags.value.isUpdating : uiFlags.value.isCreating
);
const isNameValid = computed(() => Boolean(form.value.name.trim()));

const empresas = computed(() => store.getters.getEmpresas);
const playbooks = computed(() => store.getters.getPlaybooks);

// In create mode with an empresa prop, lock the empresa picker.
const isEmpresaLocked = computed(
  () => !isEdit.value && Boolean(props.empresa?.id)
);

const resetForm = () => {
  form.value = {
    name: props.project?.name || '',
    description: props.project?.description || '',
    status: props.project?.status || 'draft',
    health: props.project?.health || 'unknown',
    priority: props.project?.priority || 'medium',
    empresa_id:
      props.project?.empresa_id ??
      props.project?.empresa?.id ??
      props.empresa?.id ??
      null,
    start_date: props.project?.start_date || '',
    target_end_date: props.project?.target_end_date || '',
    budget_amount: props.project?.budget_amount ?? '',
    playbook_id: null,
  };
};

watch(() => props.project, resetForm, { immediate: true });
watch(() => props.empresa, resetForm);

const loadPickers = () => {
  if (!empresas.value.length) store.dispatch('fetchEmpresas');
  if (!playbooks.value.length) store.dispatch('fetchPlaybooks');
};

const open = () => {
  resetForm();
  loadPickers();
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

const buildPayload = () => {
  const payload = {
    name: form.value.name.trim(),
    description: form.value.description.trim(),
    status: form.value.status,
    health: form.value.health,
    priority: form.value.priority,
  };
  if (form.value.empresa_id) payload.empresa_id = Number(form.value.empresa_id);
  if (form.value.start_date) payload.start_date = form.value.start_date;
  if (form.value.target_end_date) {
    payload.target_end_date = form.value.target_end_date;
  }
  if (form.value.budget_amount !== '' && form.value.budget_amount !== null) {
    payload.budget_amount = Number(form.value.budget_amount);
  }
  return payload;
};

const submit = async () => {
  if (!isNameValid.value || isSaving.value) return;

  const payload = buildPayload();

  try {
    if (isEdit.value) {
      const updated = await store.dispatch('updateProject', {
        id: props.project.id,
        ...payload,
      });
      useAlert(t('PROJECTS.API.UPDATE_SUCCESS'));
      emit('updated', updated);
    } else {
      const created = await store.dispatch('createProject', payload);
      // Optionally apply a playbook to the freshly created project.
      let result = created;
      if (form.value.playbook_id) {
        try {
          result = await store.dispatch('applyPlaybook', {
            id: created.id,
            playbookId: Number(form.value.playbook_id),
          });
          useAlert(t('PROJECTS.PLAYBOOKS.APPLY_SUCCESS'));
        } catch (error) {
          useAlert(t('PROJECTS.PLAYBOOKS.APPLY_ERROR'));
        }
      }
      useAlert(t('PROJECTS.API.CREATE_SUCCESS'));
      emit('created', result);
    }
    close();
  } catch (error) {
    useAlert(t('PROJECTS.API.ERROR'));
  }
};

onMounted(loadPickers);

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="lg"
    :title="isEdit ? t('PROJECTS.EDIT_TITLE') : t('PROJECTS.CREATE_TITLE')"
    :confirm-button-label="
      isEdit ? t('PROJECTS.ACTIONS.SAVE') : t('PROJECTS.ACTIONS.CREATE')
    "
    :disable-confirm-button="!isNameValid"
    :is-loading="isSaving"
    @confirm="submit"
  >
    <div class="flex flex-col gap-5">
      <!-- Name (hero) -->
      <input
        v-model="form.name"
        type="text"
        :aria-label="t('PROJECTS.FIELDS.NAME')"
        :placeholder="t('PROJECTS.NAME_PLACEHOLDER')"
        class="w-full text-lg font-semibold leading-snug bg-transparent rounded-md reset-base text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:bg-n-alpha-1 focus:px-2 focus:-mx-2 focus:py-1"
        @keyup.enter="submit"
      />

      <!-- Description -->
      <div class="flex flex-col gap-1 min-w-0">
        <span class="text-xs text-n-slate-11">
          {{ t('PROJECTS.FIELDS.DESCRIPTION') }}
        </span>
        <textarea
          v-model="form.description"
          rows="2"
          :placeholder="t('PROJECTS.FIELDS.DESCRIPTION')"
          class="w-full px-2.5 py-1.5 text-sm border rounded-md reset-base resize-none bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
        />
      </div>

      <!-- Status / Health / Priority -->
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div class="flex flex-col gap-1 min-w-0">
          <span class="text-xs text-n-slate-11">
            {{ t('PROJECTS.FIELDS.STATUS') }}
          </span>
          <select
            v-model="form.status"
            class="w-full h-8 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
          >
            <option v-for="opt in STATUS_OPTIONS" :key="opt" :value="opt">
              {{ t(`PROJECTS.STATUS.${opt.toUpperCase()}`) }}
            </option>
          </select>
        </div>
        <div class="flex flex-col gap-1 min-w-0">
          <span class="text-xs text-n-slate-11">
            {{ t('PROJECTS.FIELDS.HEALTH') }}
          </span>
          <select
            v-model="form.health"
            class="w-full h-8 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
          >
            <option v-for="opt in HEALTH_OPTIONS" :key="opt" :value="opt">
              {{ t(`PROJECTS.HEALTH.${opt.toUpperCase()}`) }}
            </option>
          </select>
        </div>
        <div class="flex flex-col gap-1 min-w-0">
          <span class="text-xs text-n-slate-11">
            {{ t('PROJECTS.FIELDS.PRIORITY') }}
          </span>
          <select
            v-model="form.priority"
            class="w-full h-8 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
          >
            <option v-for="opt in PRIORITY_OPTIONS" :key="opt" :value="opt">
              {{ t(`PROJECTS.PRIORITY.${opt.toUpperCase()}`) }}
            </option>
          </select>
        </div>
      </div>

      <!-- Empresa + Budget -->
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div class="flex flex-col gap-1 min-w-0">
          <span class="text-xs text-n-slate-11">
            {{ t('PROJECTS.FIELDS.EMPRESA') }}
          </span>
          <select
            v-model="form.empresa_id"
            :disabled="isEmpresaLocked"
            class="w-full h-8 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand disabled:opacity-60"
          >
            <option :value="null">{{ t('PROJECTS.FIELDS.NO_EMPRESA') }}</option>
            <option v-for="emp in empresas" :key="emp.id" :value="emp.id">
              {{ emp.name }}
            </option>
          </select>
        </div>
        <div class="flex flex-col gap-1 min-w-0">
          <span class="text-xs text-n-slate-11">
            {{ t('PROJECTS.FIELDS.BUDGET') }}
          </span>
          <input
            v-model="form.budget_amount"
            type="number"
            min="0"
            step="0.01"
            :placeholder="t('PROJECTS.FIELDS.BUDGET')"
            class="w-full h-8 px-2.5 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
          />
        </div>
      </div>

      <!-- Dates -->
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div class="flex flex-col gap-1 min-w-0">
          <span class="text-xs text-n-slate-11">
            {{ t('PROJECTS.FIELDS.START_DATE') }}
          </span>
          <input
            v-model="form.start_date"
            type="date"
            class="w-full h-8 px-2.5 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
          />
        </div>
        <div class="flex flex-col gap-1 min-w-0">
          <span class="text-xs text-n-slate-11">
            {{ t('PROJECTS.FIELDS.DUE_DATE') }}
          </span>
          <input
            v-model="form.target_end_date"
            type="date"
            class="w-full h-8 px-2.5 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
          />
        </div>
      </div>

      <!-- Playbook picker (create only) -->
      <div v-if="!isEdit" class="flex flex-col gap-1 min-w-0">
        <span class="flex items-center gap-1.5 text-xs text-n-slate-11">
          <span class="i-lucide-book-open size-3.5 text-n-slate-10" />
          {{ t('PROJECTS.PLAYBOOKS.TITLE') }}
        </span>
        <select
          v-model="form.playbook_id"
          class="w-full h-8 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
        >
          <option :value="null">
            {{ t('PROJECTS.PLAYBOOKS.PICKER_PLACEHOLDER') }}
          </option>
          <option v-for="pb in playbooks" :key="pb.id" :value="pb.id">
            {{ pb.name }}
          </option>
        </select>
      </div>
    </div>
  </Dialog>
</template>
